/**
 * Per-session team submissions: text, link, image.
 * Owner can mark a "winner". Any member can vote.
 *
 * Lazy-loaded when expanded so we don't fan out N requests per board.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Avatar } from './Avatar';
import { Spinner } from './Spinner';
import { UiNode } from '../lib/UiNode';
import {
  IconImage,
  IconLink,
  IconChat,
  IconTrash,
  IconStar,
  IconHeart,
  IconCheck,
} from './Icon';
import type { BoardDetail, SessionSubmission } from '../../shared/types';

export function SessionSubmissions({
  board,
  sessionId,
}: {
  board: BoardDetail;
  sessionId: string;
}) {
  const [subs, setSubs] = useState<SessionSubmission[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const isOwner = board.role === 'owner';

  useEffect(() => {
    let cancelled = false;
    void api
      .listSubmissions(board.id, sessionId)
      .then((r) => {
        if (!cancelled) setSubs(r.submissions);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed');
      });
    return () => {
      cancelled = true;
    };
  }, [board.id, sessionId]);

  async function addText() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await api.createSubmission(board.id, sessionId, { kind: 'text', text: t });
      setText('');
      const r = await api.listSubmissions(board.id, sessionId);
      setSubs(r.submissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }
  async function addLink() {
    const u = linkUrl.trim();
    if (!u || busy) return;
    setBusy(true);
    try {
      await api.createSubmission(board.id, sessionId, { kind: 'link', url: u });
      setLinkUrl('');
      const r = await api.listSubmissions(board.id, sessionId);
      setSubs(r.submissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }
  async function addImage(file: File) {
    if (busy) return;
    setBusy(true);
    try {
      await api.uploadSubmissionImage(board.id, sessionId, file);
      const r = await api.listSubmissions(board.id, sessionId);
      setSubs(r.submissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string) {
    if (!confirm('Delete this submission?')) return;
    try {
      await api.deleteSubmission(board.id, sessionId, id);
      setSubs((cur) => (cur ?? []).filter((s) => s.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }
  async function vote(id: string) {
    setSubs((cur) =>
      (cur ?? []).map((s) => (s.id === id ? { ...s, voteCount: s.voteCount + 1 } : s)),
    );
    try {
      await api.voteSubmission(board.id, sessionId, id);
    } catch {
      /* swallow — best-effort */
    }
  }
  async function toggleWinner(id: string) {
    if (!isOwner) return;
    try {
      const r = await api.toggleSubmissionWinner(board.id, sessionId, id);
      setSubs((cur) =>
        (cur ?? []).map((s) =>
          s.id === id
            ? { ...s, isWinner: r.isWinner }
            : r.isWinner
              ? { ...s, isWinner: false }
              : s,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }

  if (!subs && !error) {
    return (
      <div className="flex items-center gap-2 border-t border-ink-rim/60 pt-2 text-xs text-cream-3">
        <Spinner size={12} />
        <span>Loading submissions…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 border-t border-ink-rim/60 pt-2">
      <header className="flex items-center gap-1.5 text-xs text-cream-3">
        <IconChat size={12} />
        <span>Team submissions</span>
        <span className="ml-auto">{subs?.length ?? 0}</span>
      </header>

      {subs && subs.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {subs.map((s) => (
            <SubmissionRow
              key={s.id}
              s={s}
              board={board}
              isOwner={isOwner}
              onVote={() => vote(s.id)}
              onToggleWinner={() => toggleWinner(s.id)}
              onRemove={() => remove(s.id)}
            />
          ))}
        </ul>
      )}

      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
        <input
          className="input text-sm"
          placeholder="Add a comment…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void addText();
            }
          }}
        />
        <UiNode
          as="button"
          name="submission-add-text-button"
          entity="submission"
          action="create"
          className="btn btn-ghost"
          type="button"
          onClick={addText}
          disabled={!text.trim() || busy}
        >
          Post
        </UiNode>
        <button
          type="button"
          className="btn btn-ghost inline-flex items-center gap-1"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          title="Upload an image"
        >
          <IconImage size={14} />
        </button>
      </div>
      <div className="flex gap-2">
        <input
          className="input flex-1 text-sm"
          placeholder="https://…"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />
        <UiNode
          as="button"
          name="submission-add-link-button"
          entity="submission"
          action="create"
          className="btn btn-ghost inline-flex items-center gap-1"
          type="button"
          onClick={addLink}
          disabled={!linkUrl.trim() || busy}
        >
          <IconLink size={12} />
          Add link
        </UiNode>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void addImage(f);
          e.currentTarget.value = '';
        }}
      />
      {error && <p className="text-xs text-rust">{error}</p>}
    </div>
  );
}

function SubmissionRow({
  s,
  board,
  isOwner,
  onVote,
  onToggleWinner,
  onRemove,
}: {
  s: SessionSubmission;
  board: BoardDetail;
  isOwner: boolean;
  onVote: () => void;
  onToggleWinner: () => void;
  onRemove: () => void;
}) {
  const author = board.members.find((m) => m.id === s.authorId);
  return (
    <li
      className="rounded-lg border bg-ink-3 p-2"
      style={{
        borderColor: s.isWinner ? 'var(--color-clove)' : 'var(--color-ink-rim)',
      }}
    >
      <header className="flex items-center gap-2 text-xs text-cream-3">
        {author && <Avatar user={author} size={18} />}
        <span>{author?.name ?? 'Anonymous'}</span>
        <span className="ml-auto">{s.kind}</span>
        {s.isWinner && (
          <span className="chip inline-flex items-center gap-1 text-clove">
            <IconCheck size={10} /> Winner
          </span>
        )}
      </header>
      <div className="mt-1.5 text-sm">
        {s.kind === 'text' && s.text}
        {s.kind === 'link' && (
          <a className="text-clove underline" href={s.url ?? '#'} target="_blank" rel="noreferrer noopener">
            {s.title ?? s.url}
          </a>
        )}
        {s.kind === 'image' && s.imageUrl && (
          <img src={s.imageUrl} alt="" className="max-h-48 rounded-md" />
        )}
      </div>
      <footer className="mt-1.5 flex items-center gap-2 text-xs text-cream-3">
        <button type="button" className="btn btn-ghost inline-flex items-center gap-1 p-1" onClick={onVote} title="Upvote">
          <IconHeart size={12} />
          {s.voteCount}
        </button>
        {isOwner && (
          <button
            type="button"
            className="btn btn-ghost inline-flex items-center gap-1 p-1"
            onClick={onToggleWinner}
            title={s.isWinner ? 'Unmark winner' : 'Mark as winner'}
          >
            <IconStar size={12} />
            {s.isWinner ? 'Unmark' : 'Winner'}
          </button>
        )}
        <button type="button" className="btn btn-ghost inline-flex items-center gap-1 p-1 text-rust" onClick={onRemove} title="Delete">
          <IconTrash size={12} />
        </button>
      </footer>
    </li>
  );
}
